"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

export interface QuotationSignatory {
  id: number;
  name: string;
  mobile: string;
}

interface Props {
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  helperText: string;
  /** Which signatory field this picker displays and matches against. */
  field: "name" | "mobile";
  value: string;
  signatories: QuotationSignatory[];
  onSelect: (signatory: QuotationSignatory) => void;
  /** Keeps free-text entry available for signatories not in the master list. */
  onCustomValue: (value: string) => void;
  onClear: () => void;
}

export const SignatoryPicker: React.FC<Props> = ({
  label,
  placeholder,
  searchPlaceholder,
  helperText,
  field,
  value,
  signatories,
  onSelect,
  onCustomValue,
  onClear,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setSearch("");
      return;
    }
    const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [open]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return signatories;
    return signatories.filter(
      (signatory) =>
        signatory.name.toLowerCase().includes(query) ||
        signatory.mobile.toLowerCase().includes(query),
    );
  }, [search, signatories]);

  const trimmedSearch = search.trim();
  const showCustomOption =
    trimmedSearch !== "" &&
    !signatories.some(
      (signatory) => signatory[field].toLowerCase() === trimmedSearch.toLowerCase(),
    );

  return (
    <div className="bg-white p-4 rounded-xl border-2 border-slate-200 shadow-sm">
      <Label className="font-bold text-gray-800 mb-2 block">{label}</Label>

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`flex h-11 w-full items-center justify-between gap-2 rounded-lg border-2 px-3 text-left font-semibold transition-colors ${
            open ? "border-blue-500 ring-2 ring-blue-100" : "border-gray-300 hover:border-blue-400"
          }`}
        >
          <span className={`truncate ${value ? "text-gray-800" : "text-gray-400 font-medium"}`}>
            {value || placeholder}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {value && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear selection"
                onClick={(event) => {
                  event.stopPropagation();
                  onClear();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    onClear();
                  }
                }}
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown
              className={`h-4 w-4 text-blue-600 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </span>
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border-2 border-blue-200 bg-white shadow-xl">
              <div className="border-b border-blue-100 bg-blue-50/60 p-2.5">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    ref={searchRef}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setOpen(false);
                      if (event.key === "Enter") {
                        event.preventDefault();
                        if (filtered.length > 0) {
                          onSelect(filtered[0]);
                          setOpen(false);
                        } else if (showCustomOption) {
                          onCustomValue(trimmedSearch);
                          setOpen(false);
                        }
                      }
                    }}
                    placeholder={searchPlaceholder}
                    className="h-10 border-2 border-blue-200 pl-9 font-medium"
                  />
                </div>
              </div>

              <div className="max-h-56 overflow-y-auto overscroll-contain p-1.5">
                {filtered.length === 0 && !showCustomOption && (
                  <p className="px-3 py-6 text-center text-sm text-gray-500">
                    {signatories.length === 0
                      ? "No signatories yet. Add them from the Signatories tab."
                      : "No signatory matches your search."}
                  </p>
                )}

                {filtered.map((signatory) => {
                  const isSelected = signatory[field] === value;
                  return (
                    <button
                      key={signatory.id}
                      type="button"
                      onClick={() => {
                        onSelect(signatory);
                        setOpen(false);
                      }}
                      className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                        isSelected ? "bg-blue-50 ring-1 ring-blue-200" : "hover:bg-gray-50"
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-bold text-gray-900">
                          {signatory.name}
                        </span>
                        <span className="block truncate text-xs text-gray-500">
                          {signatory.mobile}
                        </span>
                      </span>
                      {isSelected && <Check className="h-4 w-4 shrink-0 text-blue-600" />}
                    </button>
                  );
                })}

                {showCustomOption && (
                  <button
                    type="button"
                    onClick={() => {
                      onCustomValue(trimmedSearch);
                      setOpen(false);
                    }}
                    className="mt-1 w-full rounded-lg border-2 border-dashed border-blue-300 px-3 py-2.5 text-left text-sm font-semibold text-blue-700 hover:bg-blue-50"
                  >
                    Use "{trimmedSearch}"
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-1.5">{helperText}</p>
    </div>
  );
};

export default SignatoryPicker;
